/**
 * Kategorie-Navigation: Button im Sticky-Header
 *  - Desktop: Dropdown (mehrspaltig wenn viele Kategorien)
 *  - Mobile : Vollbild-Drawer mit Suche
 *
 * Auswahl scrollt zur Produkt-Liste + setzt ?cat=<name> in der URL,
 * damit Deep-Links funktionieren.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, LayoutGrid, Search, X } from "lucide-react";

type Cat = { name: string; count: number };

type ThemeLike = {
  accent: string;
  ink: string;
  inkMuted: string;
  cardBg: string;
  bg: string;
};

export function CategoryMenu({
  categories,
  activeCat,
  onSelect,
  theme,
  isDark,
}: {
  categories: Cat[];
  activeCat: string | null;
  onSelect: (cat: string | null) => void;
  theme: ThemeLike;
  isDark: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Outside-click + ESC schliessen
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Body-Scroll-Lock auf Mobile wenn Drawer offen
  useEffect(() => {
    if (!open) return;
    if (window.matchMedia("(max-width: 767px)").matches) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, search]);

  if (categories.length === 0) return null;

  const handleSelect = (cat: string | null) => {
    onSelect(cat);
    setOpen(false);
    setSearch("");
    // Zur Produktliste scrollen
    const target = document.getElementById("produkte");
    if (target) {
      const headerOffset = 60;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 md:gap-1.5 rounded-lg px-2.5 md:px-3 py-1.5 text-xs md:text-sm font-semibold border whitespace-nowrap"
        style={{
          color: activeCat ? "#fff" : theme.ink,
          background: activeCat ? theme.accent : theme.cardBg,
          borderColor: activeCat
            ? theme.accent
            : isDark
              ? "rgba(255,255,255,.10)"
              : "rgba(0,0,0,.10)",
        }}
        aria-label="Kategorien"
        aria-expanded={open}
      >
        <LayoutGrid className="size-4" />
        <span className="hidden sm:inline truncate max-w-[120px]">
          {activeCat || "Sortiment"}
        </span>
        <ChevronDown className={`size-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Desktop Dropdown */}
      {open && (
        <div
          className="hidden md:block absolute right-0 mt-2 w-[520px] max-w-[90vw] rounded-2xl border shadow-2xl z-50 overflow-hidden"
          style={{
            background: theme.cardBg,
            borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)",
          }}
        >
          <CategoryListContent
            categories={filtered}
            allCategories={categories}
            activeCat={activeCat}
            search={search}
            setSearch={setSearch}
            onSelect={handleSelect}
            theme={theme}
            isDark={isDark}
            columns={2}
          />
        </div>
      )}

      {/* Mobile Drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-[60]" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0"
            style={{ background: "rgba(0,0,0,0.55)" }}
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute top-0 left-0 right-0 max-h-[85vh] rounded-b-3xl shadow-2xl flex flex-col"
            style={{
              background: theme.cardBg,
              color: theme.ink,
              paddingTop: "env(safe-area-inset-top, 0px)",
              animation: "dp-cat-slide-down 0.25s ease-out both",
            }}
          >
            <style>{`@keyframes dp-cat-slide-down { from { transform: translateY(-100%); } to { transform: translateY(0); } }`}</style>
            <div
              className="flex items-center justify-between px-4 py-3 border-b"
              style={{ borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)" }}
            >
              <div className="flex items-center gap-2">
                <LayoutGrid className="size-5" style={{ color: theme.accent }} />
                <h3 className="text-base font-semibold">Kategorien</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg p-1.5 hover:bg-current/5"
                aria-label="Schließen"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <CategoryListContent
                categories={filtered}
                allCategories={categories}
                activeCat={activeCat}
                search={search}
                setSearch={setSearch}
                onSelect={handleSelect}
                theme={theme}
                isDark={isDark}
                columns={1}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryListContent({
  categories,
  allCategories,
  activeCat,
  search,
  setSearch,
  onSelect,
  theme,
  isDark,
  columns,
}: {
  categories: Cat[];
  allCategories: Cat[];
  activeCat: string | null;
  search: string;
  setSearch: (s: string) => void;
  onSelect: (cat: string | null) => void;
  theme: ThemeLike;
  isDark: boolean;
  columns: 1 | 2;
}) {
  const totalCount = allCategories.reduce((sum, c) => sum + c.count, 0);

  return (
    <div className="flex flex-col">
      {/* Search nur wenn > 8 Kategorien */}
      {allCategories.length > 8 && (
        <div
          className="p-3 border-b sticky top-0 z-10"
          style={{
            background: theme.cardBg,
            borderColor: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)",
          }}
        >
          <div
            className="flex items-center gap-2 rounded-xl px-3 py-2 border"
            style={{
              borderColor: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.10)",
              background: isDark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.02)",
            }}
          >
            <Search className="size-4" style={{ color: theme.inkMuted }} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kategorie suchen…"
              className="flex-1 bg-transparent outline-none text-sm"
              style={{ color: theme.ink }}
              autoFocus={columns === 1}
            />
            {search && (
              <button onClick={() => setSearch("")} aria-label="Leeren">
                <X className="size-4" style={{ color: theme.inkMuted }} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* „Alle" Eintrag */}
      <button
        onClick={() => onSelect(null)}
        className={`text-left px-4 py-2.5 border-b transition ${
          activeCat === null ? "" : "hover:bg-current/5"
        }`}
        style={{
          borderColor: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)",
          background: activeCat === null ? theme.accent + "12" : "transparent",
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <span
            className={`text-sm ${activeCat === null ? "font-bold" : "font-medium"}`}
            style={{ color: activeCat === null ? theme.accent : theme.ink }}
          >
            Alle Produkte
          </span>
          <span className="text-xs tabular-nums" style={{ color: theme.inkMuted }}>
            {totalCount}
          </span>
        </div>
      </button>

      {/* Kategorien */}
      {categories.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm" style={{ color: theme.inkMuted }}>
          Keine Treffer
        </div>
      ) : (
        <div className={`grid ${columns === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
          {categories.map((c) => {
            const active = activeCat === c.name;
            return (
              <button
                key={c.name}
                onClick={() => onSelect(c.name)}
                className="text-left px-4 py-2.5 hover:bg-current/5 transition flex items-center justify-between gap-2"
                style={{
                  background: active ? theme.accent + "12" : "transparent",
                }}
              >
                <span
                  className={`text-sm truncate ${active ? "font-bold" : "font-medium"}`}
                  style={{ color: active ? theme.accent : theme.ink }}
                  title={c.name}
                >
                  {c.name}
                </span>
                <span
                  className="text-xs tabular-nums flex-shrink-0"
                  style={{ color: theme.inkMuted }}
                >
                  {c.count}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
